import { useItems } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import { CreateActionButton } from '@components/Buttons';
import { DataGrid } from '@components/DataGrid';
import { Box } from '@mui/system';
import { DataGridProProps, GridRowParams } from '@mui/x-data-grid-pro';
import { getPermissionsColumns } from '@utils/tables';
import { PERMISSIONS_FIELDS } from 'constants/global';
import React from 'react';
import { IPermission } from 'types/authorization';
import { IDataGridProps } from 'types/common';
import PermissionCreateDialog from './Dialogs/Permissions/PermissionCreateDialog';
import PermissionDeleteDialog from './Dialogs/Permissions/PermissionDeleteDialog';
import PermissionEditDialog from './Dialogs/Permissions/PermissionEditDialog';

const VISIBILITY_MODEL = {
	[PERMISSIONS_FIELDS.id]: false,
	[PERMISSIONS_FIELDS.version]: false,
	[PERMISSIONS_FIELDS.modifiedTime]: false
};

const PermissionsTable: React.FC<
	Partial<IDataGridProps & DataGridProProps>
> = ({ ...dataGridProps }) => {
	const { data, isLoading } = useItems<IPermission>(
		QueryKeys.permissions,
		API_ROUTES.v1.permissions.all
	);
	const columns = React.useMemo(() => getPermissionsColumns(), []);

	const tableActions = React.useMemo(
		() => [
			<CreateActionButton
				key="add-permission-key"
				Dialog={PermissionCreateDialog}
				buttonLabel={intl.tr('permissions.table.action.add.button')}
			/>
		],
		[]
	);

	const getRowActions = (p: GridRowParams) => {
		if (p?.row?.systemDefault) {
			return [];
		}
		return [
			{
				key: 'edit',
				label: intl.tr('button.edit'),
				Dialog: PermissionEditDialog
			},
			{
				key: 'delete',
				label: intl.tr('button.delete'),
				Dialog: PermissionDeleteDialog
			}
		];
	};

	return (
		<Box display="flex" flexGrow={1} data-testid="permissions-table">
			<DataGrid
				checkboxSelection={false}
				noDataMessageId="grid.data.no.data.permissions"
				rows={data?.data || []}
				loading={isLoading}
				getRowActions={getRowActions}
				columns={columns}
				columnVisibilityModel={VISIBILITY_MODEL}
				actions={tableActions}
				initialState={{
					sorting: {
						sortModel: [{ field: PERMISSIONS_FIELDS.createdTime, sort: 'desc' }]
					}
				}}
				{...dataGridProps}
			/>
		</Box>
	);
};

export default PermissionsTable;
